// src/App.js
import { useEffect, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { useSelector } from "react-redux";
import { selectIsAuthenticated, selectUser } from "./store/authSlice";
import { io } from "socket.io-client";
import API from "./api";

import Login from "./pages/Login";
import Index from "./pages";
import Register from "./pages/Register";
import SendEmail from "./pages/SendEmail";
import Leads from "./pages/Lead/Leads";
import SendSMS from "./components/Twilio/SendSMS";

import TopBar from "./components/TopBar";
import Sidebar from "./components/Sidebar";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import NotificationsFeed from "./pages/NotificationsFeed";
import Teams from "./pages/Teams";
import UserProfile from "./pages/UserProfile";
import Documents from "./pages/Documents/documents";
import AdminIndex from "./pages/AdminPages/index.jsx";
import Notes from "./pages/Notes/notes.jsx";
import Support from "./pages/Support/support.jsx";
import SetPassword from "./pages/AdminPages/SetPassword.jsx";
import ForgotPassword from "./pages/AdminPages/ForgotPassword.jsx";
import ResetPassword from "./pages/AdminPages/ResetPassword.jsx";
import LeadDetailsModal from "./pages/Lead/LeadDetailsModal";

// Format calldate (YYYY-MM-DD) + calltime (HH:MM:SS) → "MM-DD-YYYY HH:MM"
function formatNotifDateTime(calldate, calltime) {
  const [y, m, d] = (calldate || "").split(/[-T]/);
  const date = y && m && d ? `${m}-${d}-${y}` : calldate || "";
  const time = (calltime || "").slice(0, 5);
  return `${date} ${time}`.trim();
}

// Guard for private routes
function ProtectedRoute() {
  const isAuthed = useSelector(selectIsAuthenticated);
  const location = useLocation();
  return isAuthed ? (
    <Outlet />
  ) : (
    <Navigate
      to={`/login?next=${encodeURIComponent(
        location.pathname + location.search
      )}`}
      replace
    />
  );
}

// Guard for public-only routes (e.g., login/register)
function PublicRoute() {
  const isAuthed = useSelector(selectIsAuthenticated);
  const location = useLocation();
  return isAuthed ? (
    <Navigate
      to={new URLSearchParams(location.search).get("next") || "/"}
      replace
    />
  ) : (
    <Outlet />
  );
}

// Layout that conditionally renders the app chrome
function Layout() {
  const location = useLocation();
  const authPaths = ["/login", "/register", "/set-password", "/forgot-password", "/reset-password"];
  const hideChrome = authPaths.some(
    (p) => location.pathname === p || location.pathname === p + "/"
  );

  if (hideChrome) {
    return <Outlet />;
  }

  return (
    <>
      <TopBar />
      <div className="app-shell">
        <Sidebar />
        <main className="app-main">
          <Outlet />
        </main>
      </div>
    </>
  );
}

function App() {
  const user = useSelector(selectUser);
  const ass = user?.ass ?? 2;
  const isAuthed = useSelector(selectIsAuthenticated);

  // Lead modal state — opened when user clicks a desktop notification
  const [leadModal, setLeadModal] = useState({ open: false, lead: null, loading: false, error: "" });

  // Stable ref so the notification click handler always has the latest setter
  const openLeadRef = useRef(null);
  openLeadRef.current = async (leadId) => {
    if (!leadId) return;
    window.focus();
    setLeadModal({ open: true, lead: null, loading: true, error: "" });
    try {
      const resp = await API.get(`/auth/lead/${leadId}`);
      const lead = resp?.data?.lead || resp?.data;
      setLeadModal({ open: true, lead: lead || null, loading: false, error: "" });
    } catch {
      setLeadModal({ open: true, lead: null, loading: false, error: "Could not load lead details." });
    }
  };

  useEffect(() => {
    if (typeof Notification !== "undefined" && Notification.permission !== "granted") {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  // Desktop notification poller — uses ?since= so only notifications created after page load trigger OS alerts
  useEffect(() => {
    if (!isAuthed) return;

    let since = new Date().toISOString();

    const poll = async () => {
      try {
        const resp = await API.get("/notifications", { params: { ass, since } });
        const rows = Array.isArray(resp?.data) ? resp.data : [];
        if (rows.length === 0) return;

        const newest = rows.map((r) => new Date(r.created_at).getTime()).filter(Number.isFinite);
        if (newest.length) since = new Date(Math.max(...newest) + 1).toISOString();

        if (Notification.permission === "granted") {
          rows.forEach((n) => {
            try {
              const notif = new Notification(`📞 ${n.name} (${n.bname || ""})`, {
                body: `${n.comment || ""} • Agent: ${n.agent || ""} • ${formatNotifDateTime(n.calldate, n.calltime)}`,
                icon: "/favicon.ico",
                tag: String(n.lead_id),
              });
              notif.onclick = () => openLeadRef.current?.(n.lead_id);
            } catch (e) {
              console.error("[desktop-notif] Notification() failed:", e);
            }
          });
        }
      } catch (e) {
        console.error("[desktop-notif] poll error:", e);
      }
    };

    const t = setInterval(poll, 20_000);
    return () => clearInterval(t);
  }, [isAuthed, ass]);

  // Global listener: lead poller emits failures for new rows from any source
  useEffect(() => {
    const base = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";
    const socket = io(base, { withCredentials: true });

    socket.on("lead:automate:error", ({ leadName, errors }) => {
      if (!Array.isArray(errors)) return;
      errors.forEach(({ message }) => {
        toast.error(`${leadName}: ${message}`, { autoClose: 8000 });
      });
    });

    return () => socket.disconnect();
  }, []);

  return (
    <Router>
      <Routes>
        <Route element={<Layout />}>
          <Route element={<PublicRoute />}>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
          </Route>

          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/set-password" element={<SetPassword />} />

          <Route element={<ProtectedRoute />}>
            <Route path="/" element={<Index />} />
            <Route path="/send-email" element={<SendEmail />} />
            <Route path="/twilio/send" element={<SendSMS />} />
            <Route path="/leads" element={<Leads />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/callback-notifer" element={<NotificationsFeed />} />
            <Route path="/team" element={<Teams />} />
            <Route path="/profile" element={<UserProfile />} />
            <Route path="/admin" element={<AdminIndex />} />
            <Route path="/notes" element={<Notes />} />
            <Route path="/support" element={<Support />} />
          </Route>

          <Route path="*" element={<Navigate to="/login" replace />} />
        </Route>
      </Routes>

      <ToastContainer position="top-right" autoClose={5000} />

      {/* Lead modal triggered by desktop notification click */}
      <LeadDetailsModal
        open={leadModal.open}
        loading={leadModal.loading}
        error={leadModal.error}
        lead={leadModal.lead}
        onClose={() => setLeadModal((s) => ({ ...s, open: false }))}
      />
    </Router>
  );
}

export default App;
